import { Blocks } from '@/types/application';
import { Detail } from '@/types/detail';
import { Block, Configuration, ConfigurationEnum, ConfigurationMultiple, Metadata } from '@/types/metadata/metadata';
import { DetailComparison, MigrationReport } from '@/types/migration';
import { Subroutine } from '@/types/subroutine';
import {
  isArray,
  isConfigurationEnum,
  isConfigurationMultiple,
  isNullOrUndefined,
  isNumber,
  isObject
} from '../typeGuards';
import { reportChanges } from './report';

export const updateToFormat4 = (application: any, metadata: Metadata, report: MigrationReport) => ({
  ...application,
  format: 4,
  subroutines: removeSubroutineIndexes(application.subroutines, report),
  blocks: {
    ...application.blocks,
    details: removeDetailsIndexes(application.blocks.details, application.blocks.nodes, metadata, report)
  }
});

const removeSubroutineIndexes = (
  subroutines: (Subroutine & { _index_event_args?: number })[],
  report: MigrationReport
): Subroutine[] => {
  let removed = 0;
  const updated = subroutines.map((subroutine) => {
    if (!isNullOrUndefined(subroutine['_index_event_args'])) {
      delete subroutine['_index_event_args'];
      removed += 1;
    }
    return subroutine;
  });

  reportChanges(report, 'Subroutine event argument index', 'stored', 'dropped', removed);
  return updated;
};

/**
 * Drops every _index_ key the old GUI stored, whatever it holds and wherever it sits. The value it names is
 * recovered from the metadata when it can be; a block type the metadata does not describe simply has nothing
 * to recover from, and keeps no key either.
 */
export const removeDetailsIndexes = (
  details: Record<string, Detail>,
  nodes: Blocks['nodes'],
  metadata: Metadata,
  report: MigrationReport
): Record<string, Detail> => {
  let removedKeys = 0;

  const stripped = Object.fromEntries(
    Object.entries(details).map(([nodeId, detail]) => {
      const nodeType = nodes[nodeId]?.type;
      const metaBlock = metadata.blocks.find((block) => block.type === nodeType);
      const result = removeDetailsIndex(detail, metaBlock, `${nodeId} - ${nodeType ?? ''}`, report);

      removedKeys += result.removed;
      return [nodeId, result.detail];
    })
  );

  reportChanges(report, 'Old GUI index keys', '_index_ entries in details', 'dropped', removedKeys);
  return stripped;
};

// Returns the detail without its index keys, and how many it dropped from it and from everything nested inside.
const removeDetailsIndex = (
  detail: Detail,
  metaBlock: Block | undefined,
  location: string,
  report: MigrationReport
): { detail: Detail; removed: number } => {
  let removed = 0;
  const stripped: Detail = { ...detail };

  Object.entries(detail).forEach(([detailKey, detailValue]) => {
    if (isObject(detailValue)) {
      const nested = removeDetailsIndex(detailValue, metaBlock, location, report);
      removed += nested.removed;
      stripped[detailKey] = nested.detail;
    } else if (isArray(detailValue)) {
      stripped[detailKey] = detailValue.map((value) => {
        if (!isObject(value)) return value;

        const nested = removeDetailsIndex(value, metaBlock, location, report);
        removed += nested.removed;
        return nested.detail;
      });
    }

    if (!detailKey.includes('_index_')) return;

    delete stripped[detailKey];
    removed += 1;
    recoverIndexedValue(detail, stripped, detailKey, metaBlock, location, report);
  });

  return { detail: removed ? stripped : detail, removed: removed };
};

// Writes what the index key said the parameter holds when it holds nothing, and reports a disagreement.
const recoverIndexedValue = (
  detail: Detail,
  stripped: Detail,
  indexKey: string,
  metaBlock: Block | undefined,
  location: string,
  report: MigrationReport
) => {
  const key = indexKey.split('_index_')[1];
  const comparison = metaBlock && findConfigValue(metaBlock.configuration, key, indexKey, detail);
  if (!comparison) return;

  const indexed = `${comparison.indexValue.label} (${comparison.indexValue.value})`;

  if (isNullOrUndefined(detail[key]) || detail[key] === '') {
    report.changes.push({ location: location, item: key, before: '(empty)', after: indexed });
    stripped[key] = comparison.indexValue.value;
  } else if (detail[key] !== comparison.indexValue.value) {
    report.issues.push({
      location: location,
      item: key,
      found: `${comparison.detailsValue?.label} (${comparison.detailsValue?.value ?? detail[key]})`,
      issue: `The old GUI setting was ${indexed}`
    });
  }
};

const findConfigValue = (
  configuration: Configuration[] | ConfigurationEnum[] | ConfigurationMultiple[],
  splitKey: string,
  indexKey: string,
  detail: Detail
): DetailComparison | undefined => {
  let result: DetailComparison | undefined = undefined;
  configuration.forEach((config) => {
    if (Array.isArray(config)) {
      const configValue = findConfigValue(config, splitKey, indexKey, detail);
      result = configValue || result;
    }
    if (isConfigurationMultiple(config)) {
      const configValue = findConfigValue(config.sections, splitKey, indexKey, detail);
      result = configValue || result;
    }

    if (isConfigurationEnum(config)) {
      const optionsIndex = detail[indexKey];
      if (isNumber(optionsIndex) && config.options[optionsIndex] && config.value === splitKey) {
        const detailsValue = config.options?.find((o) => o.value === detail[splitKey]);
        const indexValue = config.options[optionsIndex];
        if (!detail[splitKey] || detailsValue) {
          result = { detailsValue: detailsValue, indexValue: indexValue };
        }
      } else if (config.sections) {
        config.sections.forEach((section) => {
          const configValue = findConfigValue(section, splitKey, indexKey, detail);
          result = configValue || result;
        });
      }
    }
  });

  return result;
};
